import { nextTick, ref } from 'vue'

type UseModalShellOptions = {
  closeModal: (modalId: string) => void
}

const modalTransitionDurationMs = 300

export function useModalShell({ closeModal }: UseModalShellOptions) {
  const activeModalIds = ref<string[]>([])
  const shownModalIds = ref<string[]>([])

  const modalReturnFocusTargets = new Map<string, HTMLElement>()
  const modalBackdrops = new Map<string, HTMLElement>()
  const modalShowTimerIds = new Map<string, number>()
  const modalHideTimerIds = new Map<string, number>()

  function syncModalVisibility(modalId: string, visible: boolean): void {
    if (visible) {
      activateModal(modalId)
      return
    }

    deactivateModal(modalId)
  }

  function activateModal(modalId: string): void {
    const modal = document.getElementById(modalId)
    if (!modal) return

    cancelScheduledModalHide(modalId, false)

    const activeElement = document.activeElement
    if (activeElement instanceof HTMLElement && !modal.contains(activeElement)) {
      modalReturnFocusTargets.set(modalId, activeElement)
    }

    if (!activeModalIds.value.includes(modalId)) {
      activeModalIds.value = [...activeModalIds.value, modalId]
    }

    document.body.classList.add('modal-open')
    ensureModalBackdrop(modalId)

    void nextTick(() => {
      scheduleModalShow(modalId)
      focusFirstModalElement(modalId)
    })
  }

  function deactivateModal(modalId: string, restoreFocus = true): void {
    cancelScheduledModalShow(modalId)
    shownModalIds.value = shownModalIds.value.filter((shownModalId) => shownModalId !== modalId)
    modalBackdrops.get(modalId)?.classList.remove('show')

    scheduleModalHide(modalId, restoreFocus)
  }

  function finishModalHide(modalId: string, restoreFocus: boolean): void {
    modalHideTimerIds.delete(modalId)
    activeModalIds.value = activeModalIds.value.filter((activeModalId) => activeModalId !== modalId)
    removeModalBackdrop(modalId)

    if (activeModalIds.value.length === 0) {
      document.body.classList.remove('modal-open')
    }

    if (restoreFocus) {
      restoreModalTriggerFocus(modalId)
    } else {
      modalReturnFocusTargets.delete(modalId)
    }
  }

  function scheduleModalHide(modalId: string, restoreFocus: boolean): void {
    cancelScheduledModalHide(modalId, false)

    const timerId = window.setTimeout(() => {
      finishModalHide(modalId, restoreFocus)
    }, modalTransitionDurationMs)
    modalHideTimerIds.set(modalId, timerId)
  }

  function cancelScheduledModalHide(modalId: string, finishImmediately: boolean): void {
    const timerId = modalHideTimerIds.get(modalId)
    if (timerId == null) return

    window.clearTimeout(timerId)
    modalHideTimerIds.delete(modalId)

    if (finishImmediately) {
      finishModalHide(modalId, false)
    }
  }

  function ensureModalBackdrop(modalId: string): void {
    if (modalBackdrops.has(modalId)) return

    const backdrop = document.createElement('div')
    backdrop.className = 'modal-backdrop fade'
    backdrop.dataset.modalId = modalId
    backdrop.addEventListener('click', () => {
      if (topActiveModalId() === modalId) {
        closeModal(modalId)
      }
    })
    document.body.appendChild(backdrop)
    modalBackdrops.set(modalId, backdrop)
  }

  function scheduleModalShow(modalId: string): void {
    cancelScheduledModalShow(modalId)

    void document.getElementById(modalId)?.offsetHeight
    void modalBackdrops.get(modalId)?.offsetHeight

    const timerId = window.setTimeout(() => {
      modalShowTimerIds.delete(modalId)
      if (!activeModalIds.value.includes(modalId)) return

      if (!shownModalIds.value.includes(modalId)) {
        shownModalIds.value = [...shownModalIds.value, modalId]
      }
      modalBackdrops.get(modalId)?.classList.add('show')
    }, 30)
    modalShowTimerIds.set(modalId, timerId)
  }

  function cancelScheduledModalShow(modalId: string): void {
    const timerId = modalShowTimerIds.get(modalId)
    if (timerId == null) return

    window.clearTimeout(timerId)
    modalShowTimerIds.delete(modalId)
  }

  function modalIsActive(modalId: string): boolean {
    return activeModalIds.value.includes(modalId)
  }

  function modalIsShown(modalId: string): boolean {
    return shownModalIds.value.includes(modalId)
  }

  function removeModalBackdrop(modalId: string): void {
    const backdrop = modalBackdrops.get(modalId)
    if (!backdrop) return

    backdrop.remove()
    modalBackdrops.delete(modalId)
  }

  function restoreModalTriggerFocus(modalId: string): void {
    const returnFocusTarget = modalReturnFocusTargets.get(modalId)
    modalReturnFocusTargets.delete(modalId)

    if (returnFocusTarget?.isConnected) {
      returnFocusTarget.focus()
    }
  }

  function onModalKeydown(event: KeyboardEvent): void {
    const modalId = topActiveModalId()
    if (!modalId) return

    if (event.key === 'Escape') {
      event.preventDefault()
      closeModal(modalId)
      return
    }

    if (event.key === 'Tab') {
      trapModalTabKey(modalId, event)
    }
  }

  function onModalFocusIn(event: FocusEvent): void {
    const modalId = topActiveModalId()
    if (!modalId) return

    const modal = document.getElementById(modalId)
    const target = event.target
    if (!modal || !(target instanceof Node) || modal.contains(target)) return

    focusFirstModalElement(modalId)
  }

  function trapModalTabKey(modalId: string, event: KeyboardEvent): void {
    const modal = document.getElementById(modalId)
    if (!modal) return

    const focusableElements = modalFocusableElements(modal)
    const fallbackElement = focusableElements[0] ?? modal
    if (focusableElements.length === 0) {
      event.preventDefault()
      fallbackElement.focus()
      return
    }

    const firstElement = focusableElements[0] ?? fallbackElement
    const lastElement = focusableElements[focusableElements.length - 1] ?? fallbackElement
    const activeElement = document.activeElement

    if (event.shiftKey && activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
      return
    }

    if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
      return
    }

    if (!activeElement || !modal.contains(activeElement)) {
      event.preventDefault()
      fallbackElement.focus()
    }
  }

  function focusFirstModalElement(modalId: string): void {
    const modal = document.getElementById(modalId)
    if (!modal) return

    if (!modal.hasAttribute('tabindex')) {
      modal.setAttribute('tabindex', '-1')
    }

    const focusTarget = modalFocusableElements(modal)[0] ?? modal
    focusTarget.focus()
  }

  function modalFocusableElements(modal: HTMLElement): HTMLElement[] {
    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',')

    return Array.from(modal.querySelectorAll<HTMLElement>(selectors)).filter((element) => {
      return !element.closest('.hidden') && isElementVisible(element)
    })
  }

  function isElementVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element)
    return (
      style.visibility !== 'hidden' && style.display !== 'none' && element.offsetParent !== null
    )
  }

  function topActiveModalId(): string | undefined {
    return activeModalIds.value[activeModalIds.value.length - 1]
  }

  function installModalEventListeners(): void {
    document.addEventListener('keydown', onModalKeydown)
    document.addEventListener('focusin', onModalFocusIn)
  }

  function removeModalEventListeners(): void {
    document.removeEventListener('keydown', onModalKeydown)
    document.removeEventListener('focusin', onModalFocusIn)
  }

  function cleanupModalShell(): void {
    removeModalEventListeners()
    const modalIds = activeModalIds.value.slice()
    for (const modalId of modalIds) {
      cancelScheduledModalShow(modalId)
      cancelScheduledModalHide(modalId, true)
      if (activeModalIds.value.includes(modalId)) {
        finishModalHide(modalId, false)
      }
    }
  }

  return {
    activeModalIds,
    shownModalIds,
    syncModalVisibility,
    activateModal,
    deactivateModal,
    modalIsActive,
    modalIsShown,
    installModalEventListeners,
    removeModalEventListeners,
    cleanupModalShell,
    topActiveModalId,
  }
}
